import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { SmileIcon, KebabIcon } from "../../icons";
import InfoIcon from "../../atoms/InfoIcon";
import EmojiPicker from "../EmojiPicker";
import MessageActionsButton from "../../atoms/MessageActionsButton";
import classNames from "../../../utils/classNames";
import "./index.scss";

/**
 * Ephemeral actions panel for a chat message.
 * Appears on parent hover/focus. Purely presentational; delegates actions via props.
 */
export default function MessageActionsPanel({
  className = "",
  quickEmojis = ["✅", "👀", "🙌"],
  onQuickReact,
  onEmojiPickerOpenChange,
  onMore,
}) {
  const safeEmojis = useMemo(() => Array.isArray(quickEmojis) ? quickEmojis.slice(0, 3) : ["✅", "👀", "🙌"], [quickEmojis]);

  return (
    <div id="messageActions" className={classNames("message-actions", className)} role="toolbar" aria-label="Message actions">
      <div className="message-actions__quick" aria-label="Quick reactions">
        {safeEmojis.map((emoji) => (
          <MessageActionsButton
            key={emoji}
            className="message-actions__btn--quick"
            onClick={(e) => { e.stopPropagation(); onQuickReact?.(emoji); }}
            title={`React with ${emoji}`}
            ariaLabel={`React with ${emoji}`}
          >
            <span aria-hidden="true">{emoji}</span>
          </MessageActionsButton>
        ))}
      </div>
      <div className="message-actions__divider" aria-hidden="true" />
      <div className="message-actions__main">
        <InfoIcon text="Add a reaction" className="message-actions__tip">
          <div className="message-actions__emoji">
            <EmojiPicker
              onSelectEmoji={(emoji) => onQuickReact?.(emoji?.native || emoji)}
              onOpenChange={(open) => onEmojiPickerOpenChange?.(open)}
              iconAlt="Add reaction"
              overlayTrigger
            >
              <MessageActionsButton className="message-actions__btn--visual" title="React" ariaLabel="Add reaction" onClick={(e) => e.preventDefault()}>
                <SmileIcon />
                <span className="message-actions__label">React</span>
              </MessageActionsButton>
            </EmojiPicker>
          </div>
        </InfoIcon>
        <InfoIcon text="More actions" className="message-actions__tip">
          <MessageActionsButton
            onClick={(e) => { e.stopPropagation(); onMore?.(e); }}
            title="More actions"
            ariaLabel="More actions"
          >
            <KebabIcon />
          </MessageActionsButton>
        </InfoIcon>
      </div>
    </div>
  );
}

MessageActionsPanel.propTypes = {
  className: PropTypes.string,
  quickEmojis: PropTypes.arrayOf(PropTypes.string),
  onQuickReact: PropTypes.func,
  onEmojiPickerOpenChange: PropTypes.func,
  onMore: PropTypes.func,
};

MessageActionsPanel.defaultProps = {
  className: "",
  quickEmojis: ["✅", "👀", "🙌"],
  onQuickReact: undefined,
  onEmojiPickerOpenChange: undefined,
  onMore: undefined,
};
